"use client";

/**
 * CopayFailureNotice (04/WS-11 leg — Q4, 06 §4.14) — the org-facing CO-PAY
 * collection-failure notice. The org's own card failed for its CO-PAY portion
 * of a sponsored seat: org-actionable (fix the card), but NEVER a downgrade
 * announcement — Q4: no auto-downgrade in v1; the tier is untouched and the
 * copy is a prompt to act. Amber for warning/escalating, red once the
 * escalation is terminal (soft/hard lock). Rides the billing-gate status
 * fetch (props in — no extra fetch), mirroring OutstandingInvoicesBanner.
 */
import { c as _c } from "react/compiler-runtime";
import { AlertTriangle } from "lucide-react";
import { StatusBanner, StatusBannerRow, StatusBannerContent, StatusBannerActions, statusBannerColorMap } from "@/components/ui/status-banner";
import { formatBillingCurrency } from "./billing-helpers";
export interface CopayFailureView {
  readonly escalationPhase: "warning" | "escalating" | "soft_lock" | "hard_lock" | "inactive";
  readonly amountCents: number;
  readonly currency: string;
  readonly occurredAtIso: string;
}
export function CopayFailureNotice(props) {
  const $ = _c(18);
  if (!props.isAdmin || props.failure === null) {
    return null;
  }
  const {
    escalationPhase,
    amountCents,
    currency
  } = props.failure;
  const terminal = escalationPhase === "soft_lock" || escalationPhase === "hard_lock";
  let t0;
  if ($[0] !== amountCents || $[1] !== currency) {
    t0 = formatBillingCurrency(amountCents, currency);
    $[0] = amountCents;
    $[1] = currency;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const amount = t0;
  const t1 = terminal ? "red" : "amber";
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden={true} />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = terminal ? `Action required — your ${amount} co-pay payment failed` : `Your ${amount} co-pay payment didn't go through`;
  let t4;
  if ($[4] !== t3) {
    t4 = <p className="text-sm font-semibold">{t3}</p>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="mt-0.5 text-xs">Your network still covers its share and your seats are unchanged — update your payment method so your co-pay portion can collect.</p>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <StatusBannerContent><div className="flex items-start gap-2.5">{t2}<div className="min-w-0">{t4}{t5}</div></div></StatusBannerContent>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = `inline-flex items-center whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${statusBannerColorMap[terminal ? "red" : "amber"].actionButton}`;
  let t8;
  if ($[9] !== t7) {
    t8 = <StatusBannerActions><a href="/payment" className={t7}>Update payment method</a></StatusBannerActions>;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t6 || $[12] !== t8) {
    t9 = <StatusBannerRow>{t6}{t8}</StatusBannerRow>;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== props.className || $[15] !== t1 || $[16] !== t9) {
    t10 = <StatusBanner color={t1} className={props.className} data-testid="copay-failure-notice">{t9}</StatusBanner>;
    $[14] = props.className;
    $[15] = t1;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}